import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useWorld } from "@/context/WorldContext";
import { SHIP_STATUSES } from "@/lib/constants";
import { shipLabel } from "@/lib/infobox";
import { Portrait, ShipLabelBadge } from "@/components/ShipBits";

export const StatusChip = ({ ship, otp, poly, delay = 0 }) => {
  const s = SHIP_STATUSES[shipLabel(ship)];
  return (
    <span className={`chip-in relative inline-flex items-center rounded-md border px-1.5 h-5 text-[10px] font-code font-semibold ${s.cls} ${poly ? "border-dashed" : ""}`}
      style={{ animationDelay: `${delay}ms` }} data-testid={`chart-chip-${ship.id}`}>
      {s.abbr}{poly && <sup className="ml-px text-[9px]" data-testid="chart-poly-marker">+</sup>}
      {otp && <Star className="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 text-amber-300 fill-amber-300" data-testid="chart-otp-star" />}
    </span>
  );
};

const ShipDetail = ({ ship, members, byId }) => {
  const { basePath } = useWorld();
  const fans = (ship.otp_by || []).map((id) => byId[id]?.title).filter(Boolean);
  return (
    <div className="py-2 first:pt-0 last:pb-0" data-testid={`chart-detail-${ship.id}`}>
      <div className="flex items-center gap-2">
        <Link to={`${basePath}/p/${ship.id}`} className="font-display text-lg text-pink-200 hover:text-pink-100 hover:underline truncate" data-testid={`chart-detail-link-${ship.id}`}>{ship.title}</Link>
        <ShipLabelBadge label={shipLabel(ship)} />
      </div>
      {members.length > 2 && <p className="text-[11px] font-code text-slate-500">poly · {members.map((m) => byId[m]?.title.split(" ")[0]).join(" / ")}</p>}
      {fans.length > 0 && <p className="text-xs text-amber-300 flex items-center gap-1 mt-0.5"><Star className="w-3 h-3 fill-amber-300" /> OTP for {fans.join(", ")}</p>}
    </div>
  );
};

export const ChartCell = ({ a, b, entries, byId, delay }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const hover = (v) => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(v), v ? 80 : 160); };
  if (!entries.length) return <td className="border border-line" data-testid={`chart-cell-${a.id}-${b.id}`} />;
  return (
    <td className="border border-line p-0" data-testid={`chart-cell-${a.id}-${b.id}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" onClick={(e) => { e.preventDefault(); clearTimeout(timer.current); setOpen(true); }} onMouseEnter={() => hover(true)} onMouseLeave={() => hover(false)} data-testid={`chart-cell-btn-${a.id}-${b.id}`}
            className={`w-full h-full min-h-[56px] flex flex-wrap items-center justify-center content-center gap-1.5 p-1.5 transition-colors hover:bg-pink-500/[0.07] ${entries.some((e) => e.otp) ? "bg-amber-500/[0.05]" : ""}`}>
            {entries.map((e, i) => <StatusChip key={e.ship.id} ship={e.ship} otp={e.otp} poly={e.members.length > 2} delay={delay + i * 40} />)}
          </button>
        </PopoverTrigger>
        <PopoverContent onMouseEnter={() => hover(true)} onMouseLeave={() => hover(false)} className="w-72 p-3 bg-panel border-pink-500/30 divide-y divide-line" data-testid="chart-cell-popover">
          <p className="text-[10px] font-code uppercase tracking-[0.2em] text-slate-500 pb-2">{a.title} × {b.title}</p>
          {entries.map((e) => <ShipDetail key={e.ship.id} ship={e.ship} members={e.members} byId={byId} />)}
        </PopoverContent>
      </Popover>
    </td>
  );
};

export const ColHead = ({ c }) => {
  const { basePath } = useWorld();
  return (
    <th className="sticky top-0 z-20 bg-inset border-b border-line p-1.5 align-bottom font-normal">
      <Link to={`${basePath}/p/${c.id}`} className="flex flex-col items-center gap-1 group w-[72px]" data-testid={`chart-col-${c.id}`}>
        <Portrait page={c} className="ring-1 ring-amber-500/30 group-hover:ring-amber-400 transition-shadow" />
        <span className="text-[11px] leading-tight text-slate-300 group-hover:text-amber-200 line-clamp-2 text-center">{c.title}</span>
      </Link>
    </th>
  );
};

export const RowHead = ({ c }) => {
  const { basePath } = useWorld();
  return (
    <th className="sticky left-0 z-10 bg-inset border-r border-line px-2 py-1 text-left font-normal">
      <Link to={`${basePath}/p/${c.id}`} className="flex items-center gap-2 group w-[92px] sm:w-[150px]" data-testid={`chart-row-${c.id}`}>
        <Portrait page={c} size="w-7 h-7 text-[10px]" className="ring-1 ring-amber-500/30" />
        <span className="font-display text-sm sm:text-base text-amber-50 group-hover:text-amber-200 truncate">{c.title}</span>
      </Link>
    </th>
  );
};
